import { assert } from '@fe/core';
import { levelToKeyMap } from '../../app/features/settings/components/sub-pages/monitor-alert/common/common';
import {
    AircraftThresholdType,
    commonAircraftThresholdDefinitions,
    CompanyThresholdsMode,
    MonitorAircraftThreshold,
    MonitorAircraftThresholdAlerts,
    ThresholdEnableState,
    ThresholdInfo,
    ThresholdLevel
} from '../../types/thresholds';
import {
    ThresholdLevelMergeResult as ThresholdEffectiveLevel,
    ThresholdLevelMerger,
    ThresholdValidationErrors
} from './threshold-level-merger';
import { ThresholdValidator } from './threshold-validator';

type AircraftThresholdsData = { user: MonitorAircraftThreshold[]; company: MonitorAircraftThreshold[] };

export class AircraftMonitorSettingsMerger {
    protected merger = ThresholdLevelMerger.getForMode(CompanyThresholdsMode.notUsed);
    private readonly validator = new ThresholdValidator();
    protected inheritanceEnabled = false;

    private data: AircraftThresholdsData | undefined; // TODO: use Map for faster lookups
    private thresholdTypes: AircraftThresholdType[] = [];
    private thresholdDefinitions!: Record<AircraftThresholdType, ThresholdInfo>;

    constructor() {
        this.setDefinitions(commonAircraftThresholdDefinitions);
    }

    setMode(mode: CompanyThresholdsMode) {
        this.merger = ThresholdLevelMerger.getForMode(mode);
        this.inheritanceEnabled = mode === CompanyThresholdsMode.minimum || mode === CompanyThresholdsMode.template;
    }

    setData(data: AircraftThresholdsData) {
        this.data = data;
    }

    setDefinitions(thresholdDefinitions: typeof this.thresholdDefinitions) {
        this.thresholdDefinitions = thresholdDefinitions;
        this.thresholdTypes = Object.keys(thresholdDefinitions).map((x) => +x);
    }

    getData() {
        assert(this.data);
        return this.data;
    }

    getThresholdDefinitions(): Record<AircraftThresholdType, ThresholdInfo> {
        return this.thresholdDefinitions;
    }

    getThresholdsFor(getFor: 'company' | 'user'): Record<AircraftThresholdType, AircraftThresholdMergeResult> {
        assert(this.data);
        return Object.fromEntries(
            this.thresholdTypes.map(
                (type) =>
                    [
                        type,
                        this.mergeThreshold(type, this.data![getFor].find((x) => x.type === type) ?? null, this.getInherited(getFor, type))
                    ] satisfies [AircraftThresholdType, AircraftThresholdMergeResult]
            )
        ) as Record<AircraftThresholdType, AircraftThresholdMergeResult>;
    }

    updateRow(updateFor: 'company' | 'user', threshold: MonitorAircraftThreshold): AircraftThresholdMergeResult {
        assert(this.data);
        const type = threshold.type;
        this.data[updateFor] = [...this.data[updateFor]];
        const idx = this.data[updateFor].findIndex((x) => x.type === type);
        if (idx !== -1) {
            this.data[updateFor][idx] = threshold;
        } else {
            this.data[updateFor].push(threshold);
        }

        return this.mergeThreshold(type, this.data[updateFor].find((x) => x.type === type) ?? null, this.getInherited(updateFor, type));
    }

    mergeThreshold(
        type: AircraftThresholdType,
        definition: MonitorAircraftThreshold | null,
        inherited: MonitorAircraftThreshold | null
    ): AircraftThresholdMergeResult {
        const info = this.thresholdDefinitions[type];
        const result: AircraftThresholdMergeResult = {
            type,
            definition: definition,
            inherited: inherited,
            effective: {
                type: type,
                priority: this.merger.mergePriority(definition?.priority, inherited?.priority) ?? 50,
                alerts: this.mergeAlerts(definition?.alerts, inherited?.alerts),
                enableState: this.merger.mergeEnableStatus(definition?.enableState, inherited?.enableState),
                info: this.mergeLevels(definition, inherited, ThresholdLevel.info, info),
                watch: this.mergeLevels(definition, inherited, ThresholdLevel.watch, info),
                warning: this.mergeLevels(definition, inherited, ThresholdLevel.warning, info),
                critical: this.mergeLevels(definition, inherited, ThresholdLevel.critical, info),
                emergency: this.mergeLevels(definition, inherited, ThresholdLevel.emergency, info)
            }
        };
        if (result.effective) {
            this.validator.validateThreshold(result.effective, info);
        }
        return result;
    }

    private mergeLevels(
        value: MonitorAircraftThreshold | null | undefined,
        inherited: MonitorAircraftThreshold | null | undefined,
        level: ThresholdLevel,
        info: ThresholdInfo
    ): ThresholdEffectiveLevel {
        const key = levelToKeyMap[level];
        const inheritedLevel =
            this.inheritanceEnabled && this.merger.canIgnoreInheritedLevel && value?.ignoredInheritedLevels.includes(level)
                ? null
                : inherited;

        const merged = this.merger.mergeLevels(value?.[key], inheritedLevel?.[key], info);

        if (!this.validator.validateLevelMinimum(value?.[key], inheritedLevel?.[key], info)) {
            merged.errors |= ThresholdValidationErrors.companyMinimumNotMet;
        }

        return merged;
    }

    private getInherited(getFor: 'company' | 'user', type: AircraftThresholdType): MonitorAircraftThreshold | null {
        if (!this.inheritanceEnabled) {
            return null;
        }

        if (getFor === 'company') {
            return null;
        }

        return this.data!.company.find((x) => x.type === type) ?? null;
    }

    protected mergeAlerts(
        alerts: MonitorAircraftThresholdAlerts | undefined,
        inherited: MonitorAircraftThresholdAlerts | undefined
    ): MonitorAircraftThresholdAlerts {
        return {
            eventList: this.merger.mergeAlertBooleans(alerts?.eventList, inherited?.eventList),
            eventListAck: this.merger.mergeAlertBooleans(alerts?.eventListAck, inherited?.eventListAck),
            eventListImportant: this.merger.mergeAlertBooleans(alerts?.eventListImportant, inherited?.eventListImportant),
            email: this.merger.mergeAlertBooleans(alerts?.email, inherited?.email),
            logFile: this.merger.mergeAlertBooleans(alerts?.logFile, inherited?.logFile),
            popup: this.merger.mergeAlertBooleans(alerts?.popup, inherited?.popup),
            osAlert: this.merger.mergeAlertBooleans(alerts?.osAlert, inherited?.osAlert),
            overrideColor: this.merger.mergeAlertBooleans(alerts?.overrideColor, inherited?.overrideColor),
            voice: this.merger.mergeAlertBooleans(alerts?.voice, inherited?.voice),
            playSound: this.merger.mergeAlertBooleans(alerts?.playSound, inherited?.playSound),
            overrideColorHex: this.merger.mergeAlertStrings(alerts?.overrideColorHex, inherited?.overrideColorHex),
            aircraftTag: this.merger.mergeAlertBooleans(alerts?.aircraftTag, inherited?.aircraftTag),
            zoomToAircraft: this.merger.mergeAlertBooleans(alerts?.zoomToAircraft, inherited?.zoomToAircraft),
            enableMarker: this.merger.mergeAlertBooleans(alerts?.enableMarker, inherited?.enableMarker),
            alertOnScheduled: this.merger.mergeAlertBooleans(alerts?.alertOnScheduled, inherited?.alertOnScheduled),
            alertOnPlanned: this.merger.mergeAlertBooleans(alerts?.alertOnPlanned, inherited?.alertOnPlanned),
            alertOnActive: this.merger.mergeAlertBooleans(alerts?.alertOnActive, inherited?.alertOnActive)
        };
    }

    protected getAlerts(
        type: AircraftThresholdType,
        definition: MonitorAircraftThreshold | null,
        inherited: MonitorAircraftThreshold | null
    ): [MonitorAircraftThresholdAlerts | undefined | undefined, MonitorAircraftThresholdAlerts | undefined] {
        return [definition?.alerts, inherited?.alerts];
    }
}

export type ThresholdEffectiveBase = {
    priority: number;
    enableState: ThresholdEnableState;
    info: ThresholdEffectiveLevel;
    watch: ThresholdEffectiveLevel;
    warning: ThresholdEffectiveLevel;
    critical: ThresholdEffectiveLevel;
    emergency: ThresholdEffectiveLevel;
};

export type AircraftThresholdEffective = {
    type: AircraftThresholdType;
    alerts: MonitorAircraftThresholdAlerts;
} & ThresholdEffectiveBase;

export type AircraftThresholdMergeResult = {
    type: AircraftThresholdType;
    definition: MonitorAircraftThreshold | null;
    inherited: MonitorAircraftThreshold | null;
    effective: AircraftThresholdEffective | null; // Is effective ever null? I don't think so. TODO: change this to be not nullable
};

export type AircraftEffectiveThresholds = {
    user: Record<AircraftThresholdType, AircraftThresholdMergeResult>;
    company: Record<AircraftThresholdType, AircraftThresholdMergeResult>;
};
